"use client";

import { useState } from "react";
import { GripVertical, List, Plus, SlidersHorizontal, Trash2 } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    CardSortingBlock,
    SortCard,
    SortCategory,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import { DEFAULT_THEME } from "../../lib/theme";
import { createSortCard, createSortCategory } from "../../lib/blockRegistry";
import {
    AppearanceDivider,
    AppearanceReset,
    AppearanceRow,
    AppearanceStepper,
    ColorChipField,
    ToolbarDivider,
    PopoverTitle,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
} from "./blockAppearance";

const COL_MIN = 1;
const COL_MAX = 4;

/** Non-color defaults (colors follow the lomda theme). Used by the block too. */
export const CARD_SORTING_DEFAULTS = {
    columns: 2,
    gap: 12,
    borderWidth: 1,
};

export default function CardSortingSettings({
    block,
}: {
    block: CardSortingBlock;
}) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const theme = useLomdaV3Editor((s) => s.metadata.theme) ?? DEFAULT_THEME;
    const d = block.data;
    const set = (patch: Partial<CardSortingBlock["data"]>) =>
        replaceBlockData(block.id, { ...d, ...patch });

    const columns = d.columns ?? 2;
    const borderWidth = d.cardBorderWidth ?? 1;
    const bg = d.cardBackground ?? theme.card;
    const border = d.cardBorderColor ?? theme.border;

    return (
        <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
                <span className="shrink-0 text-sm !font-semibold text-foreground">
                    {t("lomda-v3-editor.interactive.card-sorting")}
                </span>
                <WidthControl blockId={block.id} />
            </div>

            <ToolbarDivider />

            <div className="flex items-center gap-3">
                <AppearancePopover
                    background={bg === "transparent" ? theme.card : bg}
                    border={border}
                    borderWidth={borderWidth}
                    columns={columns}
                    onBackground={(v) => set({ cardBackground: v })}
                    onBorder={(v) =>
                        set({
                            cardBorderColor: v,
                            ...(borderWidth === 0 ? { cardBorderWidth: 1 } : {}),
                        })
                    }
                    onBorderWidth={(v) => set({ cardBorderWidth: v })}
                    onColumns={(v) => set({ columns: v })}
                    onReset={() =>
                        set({
                            cardBackground: theme.card,
                            cardBorderColor: theme.border,
                            cardBorderWidth: CARD_SORTING_DEFAULTS.borderWidth,
                        })
                    }
                />
                <ManageListPopover
                    labelKey="lomda-v3-editor.card-sorting.manage-categories"
                    addLabelKey="lomda-v3-editor.card-sorting.add-category"
                    placeholderKey="lomda-v3-editor.card-sorting.category-placeholder"
                    rows={d.categories.map((c) => ({ id: c.id, text: c.title }))}
                    onChange={(rows) =>
                        set({
                            categories: d.categories.map((c) => ({
                                ...c,
                                title:
                                    rows.find((r) => r.id === c.id)?.text ??
                                    c.title,
                            })),
                        })
                    }
                    onAdd={() =>
                        set({
                            categories: [...d.categories, createSortCategory()],
                        })
                    }
                    onReorder={(next) =>
                        set({
                            categories: next.map(
                                (r) => d.categories.find((c) => c.id === r.id)!
                            ),
                        })
                    }
                />
                <ManageListPopover
                    labelKey="lomda-v3-editor.card-sorting.manage-cards"
                    addLabelKey="lomda-v3-editor.card-sorting.add-card"
                    placeholderKey="lomda-v3-editor.card-sorting.card-placeholder"
                    rows={d.cards.map((c) => ({ id: c.id, text: c.text }))}
                    onChange={(rows) =>
                        set({
                            cards: d.cards.map((c) => ({
                                ...c,
                                text:
                                    rows.find((r) => r.id === c.id)?.text ??
                                    c.text,
                            })),
                        })
                    }
                    onAdd={() =>
                        // New cards start unassigned at the top of the pool
                        // (mirrors preview); the author drags them into a category.
                        set({ cards: [createSortCard(""), ...d.cards] })
                    }
                    onReorder={(next) =>
                        set({
                            cards: next.map(
                                (r) => d.cards.find((c) => c.id === r.id)!
                            ),
                        })
                    }
                />
            </div>
        </div>
    );
}

function AppearancePopover({
    background,
    border,
    borderWidth,
    columns,
    onBackground,
    onBorder,
    onBorderWidth,
    onColumns,
    onReset,
}: {
    background: string;
    border: string;
    borderWidth: number;
    columns: number;
    onBackground: (v: string) => void;
    onBorder: (v: string) => void;
    onBorderWidth: (v: number) => void;
    onColumns: (v: number) => void;
    onReset: () => void;
}) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <SlidersHorizontal className="size-5" />
                    {t("lomda-v3-editor.card-sorting.appearance")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[320px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.card-sorting.appearance")}
                    </PopoverTitle>
                    <AppearanceRow label={t("lomda-v3-editor.cards.background")}>
                        <ColorChipField value={background} onChange={onBackground} />
                    </AppearanceRow>
                    <AppearanceRow label={t("lomda-v3-editor.cards.border")}>
                        <ColorChipField value={border} onChange={onBorder} />
                    </AppearanceRow>
                    <AppearanceRow
                        label={t("lomda-v3-editor.card-sorting.appearance-border-width")}
                    >
                        <div className="flex h-9 w-14 items-center justify-center rounded-lg border border-border bg-card px-2 text-sm text-foreground">
                            <input
                                type="number"
                                min={0}
                                max={8}
                                value={borderWidth}
                                onChange={(e) =>
                                    onBorderWidth(
                                        Math.max(
                                            0,
                                            parseInt(e.target.value, 10) || 0
                                        )
                                    )
                                }
                                className="w-6 bg-transparent text-center outline-none"
                            />
                            <span className="text-muted-foreground">px</span>
                        </div>
                    </AppearanceRow>
                    <AppearanceDivider />
                    <AppearanceRow label={t("lomda-v3-editor.text.columns")}>
                        <AppearanceStepper
                            value={columns}
                            onDec={() => onColumns(Math.max(COL_MIN, columns - 1))}
                            onInc={() => onColumns(Math.min(COL_MAX, columns + 1))}
                            canDec={columns > COL_MIN}
                            canInc={columns < COL_MAX}
                        />
                    </AppearanceRow>
                    <AppearanceReset onReset={onReset} />
                </div>
            </PopoverContent>
        </Popover>
    );
}

type Row = { id: string; text: string };

function ManageListPopover({
    labelKey,
    addLabelKey,
    placeholderKey,
    rows,
    onChange,
    onAdd,
    onReorder,
}: {
    labelKey: string;
    addLabelKey: string;
    placeholderKey: string;
    rows: Row[];
    onChange: (rows: Row[]) => void;
    onAdd: () => void;
    onReorder: (rows: Row[]) => void;
}) {
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const move = (from: number, to: number) => {
        if (from === to) return;
        const next = [...rows];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onReorder(next);
    };
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <List className="size-5" />
                    {t(labelKey)}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-[352px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t(labelKey)}
                    </PopoverTitle>
                    {rows.map((r, i) => (
                        <div
                            key={r.id}
                            draggable
                            onDragStart={() => setDragIndex(i)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                                if (dragIndex !== null) move(dragIndex, i);
                                setDragIndex(null);
                            }}
                            className="flex items-center gap-2.5"
                        >
                            <GripVertical className="size-5 shrink-0 cursor-grab text-muted-foreground" />
                            <input
                                value={r.text.replace(/<[^>]*>/g, "")}
                                onChange={(e) =>
                                    onChange(
                                        rows.map((x) =>
                                            x.id === r.id
                                                ? { ...x, text: e.target.value }
                                                : x
                                        )
                                    )
                                }
                                placeholder={t(placeholderKey)}
                                className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none"
                            />
                            <button
                                type="button"
                                disabled={rows.length <= 1}
                                onClick={() =>
                                    onReorder(rows.filter((x) => x.id !== r.id))
                                }
                                className="shrink-0 text-muted-foreground transition hover:text-destructive disabled:opacity-30"
                            >
                                <Trash2 className="size-5" />
                            </button>
                        </div>
                    ))}
                    <span className="h-px w-full bg-muted" />
                    <button
                        type="button"
                        onClick={onAdd}
                        className={cn(chromeBtn, "h-10 self-start")}
                    >
                        <Plus className="size-5" />
                        {t(addLabelKey)}
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
